import type { JSX, MouseEvent } from 'react';
import { AvatarBlock, FormGroup, FormParagraph, Link } from '@ksyos/design-system';
import { ManagerAuthorization } from '@common/enums';
import { FullName } from '@frontend/components/elements/common';
import { hasOneAuthorization, useUiStateContext } from '@frontend/utils';

type PractitionerAndOrganizationProps = Readonly<{
    externalName?: string | null;
    label: string;
    organization: {
        id: string;
        name: string;
        txcsId?: number;
    } | null;
    practitioner: {
        id: string;
        nameFamily?: string;
        nameFamily2?: string | null;
        nameGiven?: string | null;
        nameInfix?: string | null;
        nameInfix2?: string | null;
        nameInitials?: string | null;
        nameUsual?: string | null;
        uid?: string;
    } | null;
}>;

export function PractitionerAndOrganization(props: PractitionerAndOrganizationProps): JSX.Element {
    const { uiState, dispatchUiStateAction } = useUiStateContext();

    return (
        <FormGroup label={props.label}>
            {props.externalName ? (
                <FormParagraph>Extern: {props.externalName}</FormParagraph>
            ) : (
                <AvatarBlock
                    size="medium"
                    subtitle={
                        props.organization ? (
                            hasOneAuthorization(ManagerAuthorization.organizationRead) ? (
                                <Link
                                    color="subtle"
                                    fontWeight="medium"
                                    onClick={handleLinkClick}
                                    to={`/organizations/${props.organization.id}/view`}
                                    variant="link"
                                >
                                    {props.organization.name}
                                </Link>
                            ) : (
                                props.organization.name
                            )
                        ) : undefined
                    }
                    subtitleSuffix={
                        props.organization ? `(${props.organization.txcsId ?? props.organization.id})` : undefined
                    }
                    title={
                        props.practitioner ? (
                            hasOneAuthorization(ManagerAuthorization.practitionerRead) ? (
                                <Link
                                    color="subtle"
                                    fontWeight="medium"
                                    onClick={handleLinkClick}
                                    to={`/practitioners/${props.practitioner.id}/profile`}
                                    variant="link"
                                >
                                    <FullName {...props.practitioner} />
                                </Link>
                            ) : (
                                <FullName {...props.practitioner} />
                            )
                        ) : undefined
                    }
                    titleSuffix={
                        props.practitioner ? `(${props.practitioner.uid ?? props.practitioner.id})` : undefined
                    }
                />
            )}
        </FormGroup>
    );

    function handleLinkClick(event: MouseEvent<HTMLElement>): void {
        event.stopPropagation();
        if (uiState.isTopBarOpen) {
            dispatchUiStateAction({ isTopBarOpen: false, type: 'setIsTopBarOpen' });
        }
    }
}
